import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from './useLocation';
import { fetchPrayerTimes, PrayerTimeEntry } from '../services/prayerTimesApi';
import { isNativeApp } from '../utils/nativeApp';
import { onAppRefresh } from '../services/appRefresh';

interface PrayerTimesHookResult {
  prayerTimes: PrayerTimeEntry[];
  loading: boolean;
  error: string | null;
}

/**
 * @param enabled Entitlement gate (prayer times are Pro). When false the hook
 *   never fetches and returns an empty result — free users must hold zero real
 *   prayer data, not just hide it (founder decision 2026-07-21). Callers pass
 *   their `useFeatureAccess().prayerTimes` flag; defaults to true for the
 *   ambient consumers (Greeting/Background) whose derived output contains no
 *   prayer names, times, or countdowns.
 */
export const usePrayerTimes = (method: number, enabled: boolean = true): PrayerTimesHookResult => {
  const { userLocation, locationError } = useLocation();
  const { i18n } = useTranslation();
  const [prayerTimes, setPrayerTimes] = useState<PrayerTimeEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      // Entitlement lapsed (or never held) — drop any stale times so no
      // consumer can keep rendering them. Functional update avoids a redundant
      // re-render when the list is already empty.
      setPrayerTimes((prev) => (prev.length ? [] : prev));
      return;
    }

    const run = async () => {
      if (!userLocation) return;
      try {
        setLoading(true);
        setError(null);
        const data = await fetchPrayerTimes(userLocation.latitude, userLocation.longitude, method, i18n.language);
        if (cancelled) return;
        setPrayerTimes(data);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Unable to load prayer times');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    // iOS native app: pull-to-refresh refetches prayer times. Dead code elsewhere.
    const offRefresh = isNativeApp() ? onAppRefresh(run) : undefined;

    return () => {
      cancelled = true;
      offRefresh?.();
    };
  }, [userLocation, method, i18n.language, enabled]);

  if (!enabled) {
    return { prayerTimes: [], loading: false, error: null };
  }

  if (locationError) {
    return { prayerTimes: [], loading: false, error: locationError };
  }

  return { prayerTimes, loading, error };
};
